import { useId, useState } from "react";

/**
 * A "?" that explains on demand, and the panel it opens.
 *
 * ── WHY IT IS A DISCLOSURE AND NOT A PARAGRAPH ──
 *
 * The argument is Field's, where this pattern started: a hint always on
 * screen is noise for everybody who already knows, on a screen they use
 * repeatedly. The Inspection Schedule is the case that made it a
 * component rather than a one-off -- 189 words of prose, trimmed twice
 * and still read as too much, because trimming was the wrong lever
 * (#429, then #436). Every paragraph was individually justified; the sum
 * was never read by anybody.
 *
 * So the explanation moves off the reading path without being deleted.
 * Somebody setting a number they already understand sees a heading and
 * the control; somebody wondering what it governs presses one button.
 *
 * ── WHY EXTRACTED ──
 *
 * Field had this inline and #436 needed the same affordance on a SECTION
 * HEADING rather than a labelled control. Copying it would have meant
 * two implementations of the accessibility details -- the pairing of
 * aria-expanded and aria-controls, the dangling-reference rule below,
 * and the decision that it must be a real <button> rather than a styled
 * span so it is reachable by keyboard and announces its state. A tooltip
 * on hover would be unreachable on the phone this app is mostly used on.
 *
 * Returns BOTH pieces rather than wrapping the caller's markup, because
 * the button belongs beside a heading and the panel belongs under it --
 * and only the caller knows what sits between them.
 */
export function useHelpDisclosure(label) {
  const id = useId();
  const [open, setOpen] = useState(false);

  const button = (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      aria-controls={id}
      aria-label={`What does ${label} mean?`}
      style={{
        border: "1px solid var(--color-border)",
        background: "transparent",
        color: "var(--color-text-muted)",
        borderRadius: "50%",
        width: "1.25rem",
        height: "1.25rem",
        lineHeight: 1,
        fontSize: "var(--font-size-sm)",
        cursor: "pointer",
        padding: 0,
      }}
    >
      ?
    </button>
  );

  const panel = (children) => (open ? (
    <div id={id} style={{
      color: "var(--color-text-muted)",
      fontSize: "var(--font-size-sm)",
      borderLeft: "2px solid var(--color-border)",
      paddingLeft: "var(--space-2)",
      display: "grid",
      gap: "var(--space-2)",
    }}>
      {children}
    </div>
  ) : null);

  // `open` is returned so a caller can hand the id to aria-describedby
  // ONLY while the panel exists. Describing a control by an element that
  // is not rendered is a dangling reference; describing it by one that is
  // rendered but hidden reads the explanation to everybody whether they
  // asked or not.
  return { button, panel, open, id };
}
